import React, { useEffect } from 'react'
import { Image } from 'antd'
import { closeGlobalImagePreview, useImagePreviewStore } from '@/store/imagePreviewStore'

const hiddenPreviewHostStyle: React.CSSProperties = {
  position: 'fixed',
  width: 0,
  height: 0,
  overflow: 'hidden',
  pointerEvents: 'none',
}

const hiddenImageStyle: React.CSSProperties = {
  width: 1,
  height: 1,
  opacity: 0,
}

const GlobalImagePreview: React.FC = () => {
  const visible = useImagePreviewStore((state) => state.visible)
  const images = useImagePreviewStore((state) => state.images)
  const current = useImagePreviewStore((state) => state.current)
  const setCurrent = useImagePreviewStore((state) => state.setCurrent)

  useEffect(() => {
    if (!visible || images.length <= 1) {
      return undefined
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault()
        setCurrent(Math.max(current - 1, 0))
      }
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault()
        setCurrent(Math.min(current + 1, images.length - 1))
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [current, images.length, setCurrent, visible])

  if (!visible && images.length === 0) {
    return null
  }

  return (
    <div style={hiddenPreviewHostStyle} aria-hidden="true">
      <Image.PreviewGroup
        preview={{
          visible,
          current,
          imageRender: (originalNode, info) => {
            const rotate = images[info.current]?.rotate
            if (!rotate) {
              return originalNode
            }

            const imageNode = originalNode as React.ReactElement<{ style?: React.CSSProperties }>
            const originalStyle = imageNode.props.style || {}
            const originalTransform = typeof originalStyle.transform === 'string' ? originalStyle.transform : ''

            return React.cloneElement(imageNode, {
              style: {
                ...originalStyle,
                transform: `${originalTransform ? `${originalTransform} ` : ''}rotate(${rotate}deg)`,
              },
            })
          },
          onVisibleChange: (nextVisible) => {
            if (!nextVisible) {
              closeGlobalImagePreview()
            }
          },
          onChange: (nextCurrent) => {
            setCurrent(nextCurrent)
          },
        }}
      >
        {images.map((image, index) => (
          <Image
            key={`${image.url}-${index}`}
            src={image.url}
            alt={`preview-${index + 1}`}
            style={hiddenImageStyle}
            preview={{ mask: null }}
          />
        ))}
      </Image.PreviewGroup>
    </div>
  )
}

export default GlobalImagePreview
